import type { ReactNode } from 'react';
import {
  ALIYUN_OSS_LINKS,
  ALIYUN_OSS_SETUP_STEPS,
  ALIYUN_RAM_OSS_POLICY_EXAMPLE,
  aliyunLinksByIds,
} from '@/config/aliyunOssGuide';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

function ExternalAnchor({
  href,
  children,
  className,
}: {
  href: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'inline-flex items-center gap-1 font-medium text-teal-700 underline decoration-teal-600/35 underline-offset-2 hover:text-teal-800',
        className
      )}
    >
      {children}
      <i className="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-70" aria-hidden />
    </a>
  );
}

export function OssCdnAliyunGuide() {
  return (
    <>
      <Card title="开通指引 · 阿里云 OSS">
        <p className="mb-4 text-sm leading-relaxed text-slate-600">
          贴图虾将图片存到您自有的阿里云 OSS，用户访问图片时走 OSS 外网流量，不占您服务器带宽。下列链接在新标签页打开。
        </p>
        <ol className="space-y-4">
          {ALIYUN_OSS_SETUP_STEPS.map((step) => {
            const links = aliyunLinksByIds(step.linkIds);
            return (
              <li key={step.order} className="rounded-xl border border-slate-200/90 bg-slate-50/50 p-4">
                <p className="text-sm font-semibold text-slate-900">
                  <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-teal-600 text-xs font-bold text-white">
                    {step.order}
                  </span>
                  {step.title}
                </p>
                <p className="mt-2 pl-8 text-sm leading-relaxed text-slate-600">{step.body}</p>
                {step.formFields && step.formFields.length > 0 ? (
                  <p className="mt-1.5 pl-8 text-xs text-slate-500">
                    对应本页字段：
                    <span className="font-mono text-slate-700">{step.formFields.join('、')}</span>
                  </p>
                ) : null}
                {links.length > 0 ? (
                  <ul className="mt-2 space-y-1.5 pl-8">
                    {links.map((l) => (
                      <li key={l.id} className="text-sm">
                        <ExternalAnchor href={l.url}>{l.label}</ExternalAnchor>
                        {l.hint ? <span className="ml-1 text-xs text-slate-500">— {l.hint}</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ol>
      </Card>

      <Card title="快捷入口（新窗口打开）">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {ALIYUN_OSS_LINKS.map((l) => (
            <a
              key={l.id}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col rounded-xl border border-slate-200/90 bg-white px-3 py-2.5 text-left shadow-sm transition hover:border-teal-300/80 hover:bg-teal-50/40"
            >
              <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 group-hover:text-teal-800">
                {l.label}
                <i
                  className="fa-solid fa-arrow-up-right-from-square text-[10px] text-slate-400 group-hover:text-teal-600"
                  aria-hidden
                />
              </span>
              {l.hint ? <span className="mt-0.5 text-xs leading-snug text-slate-500">{l.hint}</span> : null}
              <span className="mt-1 truncate font-mono text-[10px] text-slate-400" title={l.url}>
                {l.url.replace(/^https?:\/\//, '')}
              </span>
            </a>
          ))}
        </div>
        <details className="mt-4 rounded-xl border border-amber-200/80 bg-amber-50/50 px-4 py-3">
          <summary className="cursor-pointer text-sm font-medium text-amber-950">
            RAM 子账号 OSS 权限策略示例（可复制到阿里云 RAM）
          </summary>
          <p className="mt-2 text-xs text-amber-900/90">
            将 <code className="rounded bg-white/80 px-1">your-bucket-name</code> 替换为实际 Bucket 名称。
          </p>
          <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-slate-900 p-3 text-xs leading-relaxed text-slate-100">
            {ALIYUN_RAM_OSS_POLICY_EXAMPLE}
          </pre>
        </details>
      </Card>
    </>
  );
}
